import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
} from '../../util/urlHelpers';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { listingFields } from '../../config/configListing';
import NamedLink from '../NamedLink/NamedLink';

import { refCorta, formatSetCell, formatRangeCell } from './mandatoCardFields';
import css from './MandatoListingCard.module.css';

/**
 * FASE2-BUSCADOR-3b — Tarjeta del ANUNCIO DE COMPRA en el listado.
 *
 * Componente propio, no una variante del de venta. Los dos anuncios guardan
 * cosas distintas —el de venta un punto por dimensión, el de compra un
 * intervalo— y la tarjeta de venta leía campos que el mandato no tiene, así
 * que salía con cinco huecos vacíos.
 *
 * NO reutiliza ListingFieldsGrid a propósito: ese componente lo comparten la
 * tarjeta de venta, TransactionPage y CheckoutPage. Adaptarlo al mandato
 * habría metido condicionales por tipo de anuncio en tres superficies que no
 * tienen nada que ver. Aquí se reutiliza su MAQUETA (las clases de rejilla y
 * celda), que es lo que hace que las dos tarjetas se vean iguales.
 */

const Cell = ({ label, value, className }) => (
  <div className={classNames(css.cell, className)}>
    <span className={css.cellLabel}>{label}</span>
    <span className={css.cellValue}>{value}</span>
  </div>
);

const opcionesDe = key => listingFields.find(f => f.key === key)?.enumOptions || [];

export const MandatoCardBody = ({ listing, intl, columns = 3 }) => {
  const { title = '', publicData = {} } = listing?.attributes || {};
  const ref = refCorta(listing?.id?.uuid);

  const perfilBruto = opcionesDe('perfil_comprador').find(
    o => o.option === publicData.perfil_comprador
  )?.label;
  const perfilLabel = perfilBruto
    ? intl.formatMessage({ id: 'MandatoListingCard.anunciante' }, { perfil: perfilBruto })
    : undefined;
  return (
    <div className={css.body}>
      <header className={css.header}>
        <h3 className={css.title}>{title}</h3>
      </header>

      {perfilLabel ? <p className={css.contexto}>{perfilLabel}</p> : null}

      <div className={classNames(css.dataBlock, columns === 2 ? css.dosColumnas : null)}>
        <Cell
          className={css.operationCell}
          label={intl.formatMessage({ id: 'MandatoListingCard.operacion' })}
          value={formatSetCell(
            'tiposOperacion',
            opcionesDe('tiposOperacion'),
            publicData.tiposOperacion,
            intl
          )}
        />
        <Cell
          label={intl.formatMessage({ id: 'MandatoListingCard.operativa' })}
          value={formatSetCell(
            'modalidades',
            opcionesDe('modalidades'),
            publicData.modalidades,
            intl
          )}
        />
        <Cell
          label={intl.formatMessage({ id: 'MandatoListingCard.facturacion' })}
          value={formatRangeCell('facturacion', publicData, intl)}
        />
        <Cell
          label={intl.formatMessage({ id: 'MandatoListingCard.ebitda' })}
          value={formatRangeCell('ebitda', publicData, intl)}
        />
        <Cell
          label={intl.formatMessage({ id: 'MandatoListingCard.empleados' })}
          value={formatRangeCell('empleados', publicData, intl)}
        />
        <Cell
          label={intl.formatMessage({ id: 'MandatoListingCard.antiguedad' })}
          value={formatRangeCell('antiguedad', publicData, intl)}
        />
      </div>
      {ref ? (
        <span className={css.ref}>
          {intl.formatMessage({ id: 'MandatoListingCard.ref' }, { ref })}
        </span>
      ) : null}
    </div>
  );
};

const MandatoListingCard = props => {
  const { listing, variant = 'default', columns = 3, className, rootClassName } = props;
  const intl = useIntl();
  const id = listing?.id?.uuid;
  const attributes = listing?.attributes || {};
  const title = attributes.title || '';
  const slug = createSlug(title);
  const classes = classNames(rootClassName || css.card, className);

  // Misma pareja de variantes que la tarjeta del anuncio de venta, para que
  // "Mis anuncios" se comporte igual con los dos tipos: la tarjeta no enlaza a
  // la ficha, sino que ofrece editar. Un borrador no tiene ficha pública que
  // enlazar, así que aquí no puede ser un enlace a ListingPage.
  if (variant === 'owner') {
    const isDraft = attributes.state === LISTING_STATE_DRAFT;
    const editType = isDraft ? LISTING_PAGE_PARAM_TYPE_DRAFT : LISTING_PAGE_PARAM_TYPE_EDIT;
    return (
      <div className={classNames(classes, css.cardOwner)}>
        <MandatoCardBody listing={listing} intl={intl} columns={columns} />
        <NamedLink
          className={css.editLink}
          name="EditListingPage"
          params={{ id, slug, type: editType, tab: 'details' }}
        >
          {intl.formatMessage({ id: 'MandatoListingCard.editar' })}
        </NamedLink>
      </div>
    );
  }

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }} ariaLabel={title}>
      <MandatoCardBody listing={listing} intl={intl} columns={columns} />
    </NamedLink>
  );
};

export default MandatoListingCard;
